import { useCheckPendingNPS, useStudentEnrollments, useStudentDetails } from '@actions';
import UserEnrollmentTimeline from '@components/EnrollmentTimeline/UserEnrollmentTimeline';
import BannerStack from '@components/V3/BannerStack';
import { banners } from '@components/V3/BannerStack/banners';
import UserHomeCard from '@components/V3/UserHomeCard';
import PageTitle from '@components/V3/Utils/PageTitle';
import YourCoursesStudent from '@components/V3/YourCoursesStudent';
import { useAuthState } from '@contexts/AuthContext';
import { ProfileBanner } from '@modules/Account/Profile';
import StudentWhatsAppGroupModal from '@components/WhatsAppGroupsModal/StudentWhatsAppGroupModal';
import MonthlyNPSModal from '@modules/Base/PendingNPS/MonthlyNPSModal';
import FeedbackModal from '@modules/Student/components/FeedbackModal/FeedbackModal';
import style from '@styles/components/reusables/page.module.scss';
import { Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { COLORS } from '../../../../v3/global';
import OngoingMeetingsSection from '../Common/OngoingMeetingsSection';
import MeetingsNeedingFeedbackStudent from './MeetingsNeedingFeedbackStudent';
import NextMeetingsStudent from './NextMeetingsStudent';

const StudentHome: React.FC = () => {
  const { data: isPendingMonthlyNPS, refetch } = useCheckPendingNPS();
  const firstUpgradeNPS = Math.random() < 0.5;

  const [showNpsModal, setShowNpsModal] = useState(false);
  const { userId, fullName, avatar, isAdmin, isMentor } = useAuthState();

  const { data: enrollments, isLoading, isError } = useStudentEnrollments();
  const { data: userDetails, isLoading: isUserDetailsLoading } = useStudentDetails(userId);

  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const finishedMeetingId = params.get('finishedMeetingId');

  useEffect(() => {
    setShowNpsModal(isPendingMonthlyNPS?.type === 'monthly' && !finishedMeetingId);
  }, [finishedMeetingId, isPendingMonthlyNPS]);

  if (isError) {
    return <Typography.Paragraph>An error has occurred!</Typography.Paragraph>;
  }

  const handleSubmit = () => {
    setShowNpsModal(true);
  };

  const hideFeedbackModalHandler = () => {
    const params = new URLSearchParams(location.search);

    params.delete('finishedMeetingId');

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  return (
    <div className={style.mentorDashboardContainer}>
      {/*LEFT CONTAINER*/}
      {!showNpsModal && <StudentWhatsAppGroupModal />}

      {showNpsModal && (
        <MonthlyNPSModal
          visible={true}
          firstUpgradeNPS={firstUpgradeNPS}
          onClose={() => {
            setShowNpsModal(false);
            refetch();
          }}
        />
      )}

      <div
        style={{
          backgroundColor: COLORS.SECTION_BACKGROUND,
          minHeight: '100vh',
        }}
        className={`${style.page} ${style.mentorDashboardLeftContainer}`}
      >
        <div className={`${style.mentorDashboardLeftContainerContent}`}>
          <div
            style={{ marginBottom: '32px' }}
            className={`${style.mentorDashboardSectionMargin} ${style.mentorWelcomeMessage}`}
          >
            <PageTitle
              title={`Welcome back, ${fullName}`}
              description="Let's learn something new today!"
            />
          </div>

          <ProfileBanner hasSubBanner compact className="mb-6" />

          {!isMentor && !isAdmin && (
            <div className="mb-6">
              <UserEnrollmentTimeline userId={userId} />
            </div>
          )}

          {!isMentor && !isAdmin && isPendingMonthlyNPS && isPendingMonthlyNPS?.type === 'monthly' && (
            <div className={`${style.feedbackBannerContainerDesktop} mb-6`}>
              <BannerStack
                handleSubmitFeedback={() => {
                  handleSubmit();
                }}
                banners={[banners[1]]}
              />
            </div>
          )}

          {!isMentor && !isAdmin && (
            <YourCoursesStudent enrollments={enrollments} isLoading={isLoading} />
          )}
        </div>
      </div>

      {/*RIGHT CONTAINER*/}
      <div
        style={{
          backgroundColor: COLORS.SECTION_BACKGROUND,
        }}
        className={`${style.page} ${style.mentorDashboardRightContainer}`}
      >
        {!isMentor && !isAdmin && isPendingMonthlyNPS && isPendingMonthlyNPS?.type === 'monthly' && (
          <div
            style={{ marginBottom: '32px', marginTop: '16px' }}
            className={style.feedbackBannerContainerMobile}
          >
            <BannerStack
              handleSubmitFeedback={() => {
                handleSubmit();
              }}
              banners={[banners[1]]}
            />
          </div>
        )}

        <div
          className={`${style.mentorDashboardSectionMargin} ${style.mentorDashboardBalanceUserContainer}`}
        >
          <div className="ml-auto">
            <UserHomeCard
              isLoading={isUserDetailsLoading}
              name={fullName}
              description={`Graduation year: ${userDetails?.graduationYear}`}
              avatar={avatar}
            />
          </div>
        </div>

        {!isMentor && !isAdmin && (
          <div>
            {/* ONGOING MEETINGS */}
            <OngoingMeetingsSection isMentor={false} />

            {/* MEETINGS NEEDING FEEDBACK */}
            <MeetingsNeedingFeedbackStudent />

            {/* SCHEDULED MEETINGS */}
            <NextMeetingsStudent />
          </div>
        )}
      </div>

      {finishedMeetingId && (
        <FeedbackModal
          openedOnMeetingEnd
          isOpen={true}
          meetingId={finishedMeetingId}
          onClose={hideFeedbackModalHandler}
        />
      )}
    </div>
  );
};

export default StudentHome;
